import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ACTIVITY_STATUS } from "@/lib/statuses";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_BRACKETS = [
  { value: "6–8 ans", key: "age.6-8" },
  { value: "9–11 ans", key: "age.9-11" },
  { value: "12–15 ans", key: "age.12-15" },
  { value: "Autre", key: "age.other" },
];

export default function InscriptionDialog({ activity, open, onClose }) {
  const { t, lang, localized } = useLang();
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    child_first_name: "",
    child_age_bracket: "",
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    availability: "",
    notes: "",
    consent_contact: false,
    consent_privacy: false,
  });

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSuccess(false);
        setForm({
          child_first_name: "",
          child_age_bracket: "",
          parent_first_name: "",
          parent_last_name: "",
          email: "",
          phone: "",
          availability: "",
          notes: "",
          consent_contact: false,
          consent_privacy: false,
        });
      }, 300);
    }
  }, [open]);

  if (!activity && !open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit =
    form.parent_first_name &&
    form.parent_last_name &&
    form.email &&
    form.child_age_bracket &&
    form.consent_contact &&
    form.consent_privacy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !activity) return;
    setBusy(true);
    try {
      await axios.post(`${API}/inscriptions`, {
        activity_slug: activity.slug,
        preferred_lang: lang,
        ...form,
      });
      setSuccess(true);
      toast.success(t("ins.toast_ok"));
    } catch (err) {
      toast.error(t("talk.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const st = activity ? ACTIVITY_STATUS[activity.status] : null;
  const displayedTitle = activity ? (activity._localized_title || localized(activity, "title")) : "";

  return (
    <AnimatePresence>
      {open && activity && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-foreground/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={onClose}
          data-testid="inscription-dialog"
        >
          <motion.div
            initial={{ y: 60, opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-2xl bg-background rounded-t-3xl md:rounded-3xl max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-background/95 backdrop-blur px-6 md:px-10 py-5 flex items-start justify-between border-b border-border z-10">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-primary">
                  {t("ins.title")}
                </div>
                <div className="mt-1 font-heading text-xl leading-tight">{displayedTitle}</div>
                {st && (
                  <div
                    className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ backgroundColor: st.bg, color: st.fg }}
                  >
                    {t(st.labelKey)}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                data-testid="inscription-close-btn"
                aria-label={t("common.close")}
                className="p-2.5 rounded-full border border-border hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 md:px-10 py-8">
              {success ? (
                <SuccessState onClose={onClose} title={displayedTitle} t={t} />
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="inscription-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField label={t("ins.parent_first")} value={form.parent_first_name} onChange={(v) => update("parent_first_name", v)} testId="ins-parent-first" required />
                    <TextField label={t("ins.parent_last")} value={form.parent_last_name} onChange={(v) => update("parent_last_name", v)} testId="ins-parent-last" required />
                  </div>
                  <TextField label={t("ins.email")} type="email" value={form.email} onChange={(v) => update("email", v)} testId="ins-email" required />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField label={t("ins.phone")} type="tel" value={form.phone} onChange={(v) => update("phone", v)} testId="ins-phone" placeholder="+32 …" />
                    <TextField label={t("ins.child_first")} value={form.child_first_name} onChange={(v) => update("child_first_name", v)} testId="ins-child-first" />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-foreground">{t("ins.child_age")}</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {AGE_BRACKETS.map((a) => (
                        <button
                          key={a.value}
                          type="button"
                          onClick={() => update("child_age_bracket", a.value)}
                          data-testid={`ins-age-${a.value.replace(/[^\w]/g, "-")}`}
                          className={`px-4 py-2.5 rounded-full font-semibold text-sm border-2 min-h-[44px] ${
                            form.child_age_bracket === a.value ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground"
                          }`}
                        >
                          {t(a.key)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <TextField label={t("ins.availability")} value={form.availability} onChange={(v) => update("availability", v)} testId="ins-availability" placeholder={t("ins.avail_placeholder")} />

                  <div>
                    <label className="block text-sm font-bold text-foreground">{t("ins.notes")}</label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      rows={3}
                      placeholder={t("ins.notes_placeholder")}
                      data-testid="ins-notes-input"
                      className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-border bg-white outline-none focus:border-primary resize-none"
                    />
                  </div>

                  <div className="space-y-3 pt-2">
                    <Checkbox label={t("ins.consent_contact")} checked={form.consent_contact} onChange={(v) => update("consent_contact", v)} testId="ins-consent-contact" />
                    <Checkbox label={t("ins.consent_privacy")} checked={form.consent_privacy} onChange={(v) => update("consent_privacy", v)} testId="ins-consent-privacy" />
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row justify-end gap-3">
                    <button type="submit" disabled={!canSubmit || busy} data-testid="inscription-submit-btn" className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 justify-center">
                      <Send className="w-4 h-4" strokeWidth={2.5} />
                      {busy ? t("ins.submitting") : t("ins.submit")}
                    </button>
                  </div>

                  <p className="text-xs text-foreground/55 leading-relaxed">{t("ins.no_medical")}</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function TextField({ label, value, onChange, testId, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-bold text-foreground">{label} {required && "*"}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-border bg-white outline-none focus:border-primary min-h-[48px]"
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange, testId }) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={testId}
      onClick={(e) => {
        if (e.target.tagName !== "INPUT") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
    >
      <span className={`shrink-0 w-6 h-6 mt-0.5 rounded-md border-2 grid place-items-center transition-colors ${
        checked ? "bg-primary border-primary" : "bg-white border-border group-hover:border-foreground/40"
      }`}>
        {checked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <span className="text-sm text-foreground/85 leading-relaxed">{label}</span>
    </label>
  );
}

function SuccessState({ onClose, title, t }) {
  return (
    <div className="text-center py-6" data-testid="inscription-success">
      <div className="mx-auto w-20 h-20 rounded-full grid place-items-center" style={{ backgroundColor: "hsl(var(--accent))" }}>
        <Check className="w-10 h-10 text-foreground" strokeWidth={3} />
      </div>
      <h3 className="mt-6 font-heading text-2xl md:text-3xl">{t("ins.success_title")}</h3>
      <p className="mt-3 text-foreground/75 leading-relaxed max-w-md mx-auto">
        {t("ins.success_text_1")} <span className="font-bold">{title}</span>.
      </p>
      <button onClick={onClose} className="mt-8 pill-btn bg-primary text-primary-foreground hover:bg-primary/90">
        {t("ins.success_back")}
      </button>
    </div>
  );
}
